import { AsyncPipe, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { ALTabsComponent } from '@asamco/application-link/shared';
import { AsamcoUserService } from '@asamco/base';
import { BehaviorSubject, Subject, merge, of } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';

type AdminTabId =
  | 'company-management'
  | 'user-company-management'
  | 'user-management'
  | 'company-access-request-management'
  | 'applications'
  | 'application-logs'
  | 'link-transfer'
  | 'mock-data'
  | 'warning-modal-messages'
  | 'joblog-pattern-matcher'
  | 'changelog-management';

const DEFAULT_TAB: AdminTabId = 'company-management';

type TabInfo = { id: AdminTabId; text: string; icon: string };

@Component({
  selector: 'app-shell-admin-tooling-overview',
  templateUrl: './admin-tooling-overview.component.html',
  styleUrls: ['./admin-tooling-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIf, AsyncPipe, RouterOutlet, ALTabsComponent],
})
export class AdminToolingOverviewComponent implements OnInit {
  public readonly isAdminSubject = new Subject<boolean>();
  public readonly isAdminObservable = this.isAdminSubject.asObservable();

  public readonly tabInfos: TabInfo[] = [
    { id: 'company-management', text: 'Companies', icon: 'far fa-hospitals' },
    { id: 'user-company-management', text: 'User companies', icon: 'far fa-hospital-user' },
    { id: 'user-management', text: 'Users', icon: 'far fa-users' },
    { id: 'company-access-request-management', text: 'Company access requests', icon: 'far fa-link' },
    { id: 'applications', text: 'Applications', icon: 'far fa-users-class' },
    { id: 'application-logs', text: 'Application Logs', icon: 'far fa-list' },
    { id: 'link-transfer', text: 'Link Transfer', icon: 'far fa-file-export' },
    { id: 'mock-data', text: 'Mock Data', icon: 'far fa-file-export' },
    { id: 'warning-modal-messages', text: 'Warning modal messages', icon: 'far fa-exclamation-triangle' },
    { id: 'joblog-pattern-matcher', text: 'Job Log Pattern Matcher', icon: 'far fa-file-export' },
    { id: 'changelog-management', text: 'Changelog', icon: 'far fa-newspaper' },
  ];

  // ALTabs expects a subject-like thing it can .next(tab.id) into
  public readonly selectedTabId$ = new BehaviorSubject<AdminTabId>(DEFAULT_TAB);

  constructor(
    private asamcoUserService: AsamcoUserService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.asamcoUserService.isAdmin().then(result => this.isAdminSubject.next(result));

    // Route -> selectedTabId$ so deep-links / refresh / back-forward keep tab highlighted
    merge(of(null), this.router.events.pipe(filter(e => e instanceof NavigationEnd)))
      .pipe(
        map(() => (this.activatedRoute.firstChild?.snapshot.url?.[0]?.path as AdminTabId | undefined) ?? DEFAULT_TAB),
        startWith((this.activatedRoute.firstChild?.snapshot.url?.[0]?.path as AdminTabId | undefined) ?? DEFAULT_TAB),
        distinctUntilChanged()
      )
      .subscribe(tab => this.selectedTabId$.next(tab));

    // Ensure we always have a child route when entering /admin-tooling-overview
    if (!this.activatedRoute.firstChild) {
      this.router.navigate([DEFAULT_TAB], { relativeTo: this.activatedRoute });
    }
  }

  // ✅ This matches your working pattern: [onTabClick]="onTabClick"
  onTabClick = (tab: TabInfo) => {
    // Update selection so ALTabs highlights immediately
    this.selectedTabId$.next(tab.id);

    // Navigate to child route (this actually swaps the router-outlet content)
    this.router.navigate([tab.id], { relativeTo: this.activatedRoute });
  };
}
